import axios, { AxiosRequestConfig, AxiosResponse, CancelTokenSource } from 'axios';
import { responseHandler as apiVersionResponse } from './api-version.interceptor';
import redirectResponse, { RedirectError } from './redirect.interceptor';
import validationErrorsResponse from './validation-errors.interceptor';
import { requestHandler as authRequest, responseHandler as authResponse } from '@/shared/auth/auth.interceptor';
import { requestHandler as referrerRequest } from './referrer.interceptor';
import { requestHandler as settingsRequest } from './settings.interceptor';
import * as querystring from 'querystring';
import { isArray, forEach } from 'lodash';
import HttpStatus from 'http-status-codes';
import { B2BContentPageViewModel } from '@/types/B2BcontentServerContract';
import messagesStore from '@/core/messages/messages.store';
import { HttpCancelError } from '@/core/http/HttpCancelError';
import { ClientMessageType } from '@/b2b/shared/api/server-apis';
import { B2CContentPageViewModel, B2CPublicContentPageViewModel } from '@/types/B2CcontentServerContract';
import { QueryKeys } from '@/shared/config/route-query.config';

// Those status'es that should cause "then" to be executed (so we can have interceptors)
const handledErrorStatusCodes = [
    HttpStatus.BAD_REQUEST,
    HttpStatus.NOT_FOUND,
    HttpStatus.INTERNAL_SERVER_ERROR,
    HttpStatus.BAD_GATEWAY,
    HttpStatus.UNAUTHORIZED,
    HttpStatus.FORBIDDEN,
];

declare module 'axios' {
    export interface AxiosRequestConfig {
        messagesId?: string;
    }
}

export class HttpService {
    private responseInterceptors: Array<
        (value: AxiosResponse<any>) => AxiosResponse<any> | Promise<AxiosResponse<any>>
        > = [apiVersionResponse, authResponse, redirectResponse, validationErrorsResponse];

    private requestInterceptors: Array<(value: AxiosRequestConfig) => AxiosRequestConfig> = [referrerRequest, settingsRequest, authRequest];

    private pageCancelTokenSrc: CancelTokenSource | null = null;

    private get defaultGetHeaders(): any {
        return ({
            'Content-Type': 'application/json', // must use this casing for the authentication service to work
        });
    }

    constructor() {
        this.registerInterceptors();

        axios.defaults.validateStatus = (status) => (status >= 200 && status < 300) || handledErrorStatusCodes.includes(status);
        axios.defaults.paramsSerializer = (params) => {
            const sortedParams = this.getSortedParams(params);
            return querystring.stringify(sortedParams);
        };
    }

    private cancellableRequestDictionary: { [key: string]: CancelTokenSource | null } = {};

    private requestSucceded(res: AxiosResponse, contextKey?: string) {
        if (contextKey) {
            const cancellableContextKey = contextKey;
            this.cancellableRequestDictionary[cancellableContextKey] = null;
            delete this.cancellableRequestDictionary[cancellableContextKey];
        }
        return res;
    }

    private ensureCancellationToken(contextKey: string) {
        const cancellableContextKey = contextKey;
        let cancelTokenSrc = this.cancellableRequestDictionary[cancellableContextKey];
        if (cancelTokenSrc) {
            cancelTokenSrc.cancel();
        }

        cancelTokenSrc = axios.CancelToken.source();
        this.cancellableRequestDictionary[cancellableContextKey] = cancelTokenSrc;

        return cancelTokenSrc;
    }

    /** Returns true if cancelled, false if no oustanding request found. */
    public cancelRequest(cancelKey: string): boolean {
        for (const key in this.cancellableRequestDictionary) {
            if (key.endsWith(cancelKey)) {
                const cancelTokenSrc = this.cancellableRequestDictionary[key];
                if (cancelTokenSrc) {
                    cancelTokenSrc.cancel();
                    this.cancellableRequestDictionary[key] = null;
                    delete this.cancellableRequestDictionary[key];
                    return true;
                }
            }
        }

        return false;
    }

    private handlePotentialErrorResponse<T>(res: AxiosResponse<T>): T {
        if (handledErrorStatusCodes.includes(res.status)) {
            // Validatestatus above is set to include these ones so it will trigger 'then'.
            // Reason is that interceptors will then be run automatically on this as well.
            if ([HttpStatus.BAD_GATEWAY, HttpStatus.INTERNAL_SERVER_ERROR].includes(res.status)) {
                // handle error codes
            }
            throw res;
        }

        return res.data;
    }

    private handleErrorResponse(error: any): void {
        const labelKey = 'global/generic/unexpectedServerError';
        if (error) {
            if (axios.isCancel(error)) {
                throw new HttpCancelError();
            }
            if (error instanceof RedirectError) {
                return;
            }
            if (error.status === HttpStatus.INTERNAL_SERVER_ERROR) {
                const message = error.data?.message ?? error;
                messagesStore.addApiMessages([{ labelKey, message, messageType: ClientMessageType.Error }]);
            }
        } else {
            messagesStore.addApiMessages([{ labelKey, message: '', messageType: ClientMessageType.Error }]);
        }
        throw error;
    }

    public async get<T>(url: string, params?: any, config?: AxiosRequestConfig, cancellationContextKey?: string): Promise<T> {
        config = config || {};
        config = {
            ...config,
            ...{
                headers: {
                    ...this.defaultGetHeaders,
                    ...config.headers,
                },
                data: {}, // Axios kills application/json if no data object is provided https://github.com/axios/axios/issues/86#issuecomment-139638284
                params,
            },
        };

        const cancelTokenSrc = cancellationContextKey ? this.ensureCancellationToken(cancellationContextKey) : undefined;

        return axios
            .get(url, { ...config, cancelToken: cancelTokenSrc?.token })
            .then(res => this.requestSucceded(res, cancellationContextKey))
            .then(res => this.handlePotentialErrorResponse(res))
            .catch(err => this.handleErrorResponse(err));
    }

    public async post<T>(url: string, payload?: any, messagesId?: string): Promise<T> {
        const config = {
            messagesId,
            headers: this.defaultGetHeaders,
        };
        return axios
            .post(url, payload, config)
            .then(res => this.handlePotentialErrorResponse(res))
            .catch(err => this.handleErrorResponse(err));
    }

    public async delete<T>(url: string, payload?: any, messagesId?: string): Promise<T> {
        const config = {
            data: payload,
            headers: this.defaultGetHeaders,
            messagesId,
        };

        return axios.delete(url, config)
            .then(res => this.handlePotentialErrorResponse(res))
            .catch(err => this.handleErrorResponse(err));
    }

    public async put<T>(url: string, payload?: any, messagesId?: string): Promise<T> {
        const config = {
            headers: this.defaultGetHeaders,
            messagesId,
        };

        return axios.put(url, payload, config)
            .then(res => this.handlePotentialErrorResponse(res))
            .catch(err => this.handleErrorResponse(err));
    }

    async getPage(url: string): Promise<B2BContentPageViewModel | B2CContentPageViewModel | B2CPublicContentPageViewModel> {
        if (this.pageCancelTokenSrc) {
            this.pageCancelTokenSrc.cancel();
        }
        this.pageCancelTokenSrc = axios.CancelToken.source();
        try {
            const res = await axios.get<B2BContentPageViewModel | B2CContentPageViewModel | B2CPublicContentPageViewModel>(url, {
                headers: this.defaultGetHeaders,
                data: {}, // Axios kills application/json if no data object is provided https://github.com/axios/axios/issues/86#issuecomment-139638284
                params: { [QueryKeys.SpaRequest]: true }, // differentiate xhr and page requests to prevent a cached response from one affecting the other - ie. json output on fresh page load
                cancelToken: this.pageCancelTokenSrc.token,
            });
            const response = this.handlePotentialErrorResponse(res);
            this.pageCancelTokenSrc = null;
            return response;
        } catch (error) {
            if (axios.isCancel(error)) {
                throw new HttpCancelError();
            }
            throw error;
        }
    }

    private registerInterceptors() {
        this.responseInterceptors.forEach((i) => axios.interceptors.response.use(i));
        this.requestInterceptors.forEach((i) => axios.interceptors.request.use(i));
    }

    private getSortedParams(params: any): any {
        const keys: string[] = [];

        forEach(params, (value, key: string) => {
            keys.push(key);
        });

        const sortedParams = {};
        const sortedKeys = keys.sort();

        sortedKeys.forEach((value) => {
            const searchValue = params[value];
            sortedParams[value] = isArray(searchValue) ? searchValue.sort() : searchValue;
        });

        return sortedParams;
    }
}

export default new HttpService();
